import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import { Workout } from '@strength/domain-workout';
import { MdClose } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import { UnrecordedActual } from '../../types';
import { trpc } from '../../../../../../trpc/react';

export const CompleteWorkoutDialog = ({
  athlete,
  teamId,
  workout,
  open,
  onClose,
  onComplete,
  unrecordedActuals,
}: {
  athlete: AthletePreview;
  teamId: string;
  workout: Workout | undefined;
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  unrecordedActuals: UnrecordedActual[];
}) => {
  const { t } = useTranslation();
  // Resolve the missing-actuals warning against the recording athlete's OWN team
  // (the multi-team tablet may span several teams with different settings).
  const { data: warnRows } = trpc.tablet.warnOnMissingActuals.useQuery(
    undefined,
    { staleTime: 0, enabled: !!teamId },
  );
  // Header-only endpoint returns a row per in-scope team, so resolve this
  // athlete's own team rather than assuming a single-team response.
  const enabledMissingActualsWarning =
    warnRows?.find((row) => row.teamId === teamId)?.warn ?? false;

  if (!workout) {
    return null;
  }

  return (
    <Dialog
      key={'complete-workout-dialog'}
      onClose={onClose}
      open={open}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle className="flex flex-row justify-between items-center">
        {t('completeWorkout')}
        <IconButton onClick={onClose} aria-label={t('close')}>
          <MdClose size={24} aria-hidden="true" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          {t('completeWorkoutConfirmation', {
            athleteName: athleteDisplayName(athlete),
          })}
        </Typography>
        {enabledMissingActualsWarning && unrecordedActuals.length > 0 && (
          <>
            <Typography variant="body1" className="mb-0">
              {t('warnOnMissingActuals')}
            </Typography>
            <ul className="mt-0">
              {unrecordedActuals.map((actual) => (
                <li key={actual.workoutExerciseId}>{actual.exerciseName}</li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            onComplete();
            onClose();
          }}
        >
          {t('complete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
